import { useGaiaWebsocket } from "@expert/gaia";

import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useAgentStore, useChatStore, useLogger, useSessionId } from "@/sdk";
import { useTypedMostRecentTask } from "@/sdk/agent/hooks/useMostRecentTask";
import { usePartner } from "@/sdk/agent/hooks/usePartner";

import type { CommunicationType } from "../types";

import { useProductOfferStore } from "../../stores";
import { type SendNotificationResponse, sendNotification } from "./api/sendNotification";

/**
 * Track sessions where compliance event has already been sent to prevent duplicate calls.
 * This prevents re-sending the event if sendNotification fails and the expert retries.
 * Module-level to persist across component unmounts/remounts.
 */
const complianceEventSentSessions = new Set<string>();

export const useHandleSendLink = () => {
    // Logger with session context (sessionId, partner, callSid) automatically included
    const logger = useLogger({ module: "HandleSendLink", supportTeam: "sales" });

    const sessionId = useSessionId();
    const agentId = useAgentStore((state) => state.agentId);
    const websocketObj = useGaiaWebsocket();
    const partner = usePartner();
    const { isRealTimeComplianceCheckEnabled } = usePartnerFeatureConfigured();

    const { isVoice, isChat, chatTask } = useTypedMostRecentTask();
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const activeChatId = useChatStore((state) => state.activeChatId);

    if (primaryProducts == null) {
        throw new Error("Expected data is undefined");
    }

    if (!isVoice && !isChat) {
        return () => Promise.reject(new Error("Send link is only available for voice or chat tasks"));
    }

    const sendComplianceEvent = () => {
        if (!isVoice || !isRealTimeComplianceCheckEnabled || !websocketObj || websocketObj.loading) {
            return;
        }

        // Skip if compliance event has already been sent for this session
        if (complianceEventSentSessions.has(sessionId)) {
            logger.debug("Compliance event already sent for this session, skipping");
            return;
        }

        const gaiaPayload = {
            action: "orchestrator-invoke",
            appName: "soluto-sales-compliance-cue",
            messageType: "Notification",
            correlationId: crypto.randomUUID(),
            messageId: crypto.randomUUID(),
            message: "",
            sessionId: activeChatId ?? sessionId,
            sessionGroupId: sessionId,
            userId: agentId,
            userType: "Expert",
            partner,
        };

        try {
            websocketObj.sendJsonMessage(gaiaPayload);
            complianceEventSentSessions.add(sessionId);
            logger.info("Successfully sent compliance validation event to GAIA");
        } catch (error) {
            logger.warn({ error }, "Failed to send compliance validation event to GAIA");
        }
    };

    return async (commType: CommunicationType, contactToSend: string): Promise<SendNotificationResponse> => {
        // Fire GAIA notification in parallel (fire-and-forget)
        sendComplianceEvent();

        // Send the actual notification
        return sendNotification(logger, {
            sessionId,
            partner,
            productOfferId: selectedProduct?.id ?? primaryProducts[0].id,
            productSku: selectedProduct?.productSku ?? primaryProducts[0].productSku,
            customerEmailId: commType === "EMAIL" && contactToSend ? contactToSend : undefined,
            customerMdn: commType === "SMS" && contactToSend ? contactToSend : undefined,
            customerChatId: activeChatId,
            workerSid: agentId,
            topic: isChat ? chatTask?.topic : undefined,
            language: isChat ? chatTask?.language : undefined,
        });
    };
};
